import { useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { emailKey, type EmailDraft } from '../../lib/email-api';
import {
  resourceApi,
  type Preview,
  type TemplateDef,
} from '../../lib/email-resources-api';
export function SaveMessageTemplate({
  subject,
  bodyText,
  addresses,
  personalFacts=[],
}: {
  subject: string;
  bodyText: string;
  addresses: string[];
  personalFacts?:string[];
}) {
  const [open, setOpen] = useState(false),
    [name, setName] = useState(''),
    [objectId, setObjectId] = useState(''),
    [subjectCopy, setSubject] = useState(subject),
    [bodyCopy, setBody] = useState(bodyText);
  const client = useQueryClient();
  const options = useQuery({
    queryKey: [...emailKey(), 'options'],
    queryFn: resourceApi.options,
    enabled: open,
    retry: false,
  });
  const object =
    options.data?.objects.find((o) => o.id === objectId) ??
    options.data?.objects.find((o) => o.name === 'person');
  const save = useMutation({
    mutationFn: () =>
      resourceApi.save('templates', {
        name,
        definition: {
          contextObjectId: object?.id ?? '',
          recipient: {
            emailFieldId:
              object?.fields.find((f) => f.type === 'emails')?.id ?? '',
          },
          message: {
            mode: 'exact',
            subject: [{ kind: 'literal', text: subjectCopy }],
            body: [{ kind: 'literal', text: bodyCopy }],
          },
        },
      }),
    onSuccess: () =>
      void client.invalidateQueries({ queryKey: [...emailKey(), 'templates'] }),
  });
  return (
    <details onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>Save as template</summary>
      {open && (
        <>
          <p>
            Copies this message privately as Exact wording. Review
            recipient-specific facts before reusing it; copied text is not
            automatically converted to variables.
          </p>
          {[...new Set([...addresses,...personalFacts,...((subjectCopy+' '+bodyCopy).match(/[$€£]\s?\d[\d,.]*|\d{4}-\d{2}-\d{2}/g)??[])])].map((a) => (
            <p key={a}>
              Review personal detail: <mark>{a}</mark>
            </p>
          ))}
          <label>
            Template name
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            Context type
            <select
              value={object?.id ?? ''}
              onChange={(e) => setObjectId(e.target.value)}
            >
              {options.data?.objects
                .filter((o) => o.fields.some((f) => f.type === 'emails'))
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Subject
            <input
              value={subjectCopy}
              onChange={(e) => setSubject(e.target.value)}
            />
          </label>
          <label>
            Body
            <textarea
              value={bodyCopy}
              onChange={(e) => setBody(e.target.value)}
            />
          </label>
          <button
            disabled={!options.data?.enabled || !name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            Save template
          </button>
          {save.data && (
            <p>
              <a href={`/app/email/templates/${save.data.id}`}>
                Open template editor to add contact details
              </a>
            </p>
          )}
          {save.error && <p role="alert">{save.error.message}</p>}
        </>
      )}
    </details>
  );
}
export function ApplyTemplateToDraft({
  draft,
  records,
  connectionId,
}: {
  draft: EmailDraft;
  connectionId: string;
  records: Array<{ recordId: string; displayName: string }>;
}) {
  const [open, setOpen] = useState(false),
    [templateId, setTemplateId] = useState(''),
    [recordId, setRecordId] = useState(records[0]?.recordId ?? ''),
    [replace, setReplace] = useState(false),
    [preview, setPreview] = useState<Preview>();
  const client = useQueryClient();
  const templates = useInfiniteQuery({
    queryKey: [...emailKey(), 'templates'],
    queryFn: ({ pageParam }) => resourceApi.list('templates', pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.nextCursor ?? undefined,
    enabled: open,
  });
  const selected = templates.data?.pages
    .flatMap((p) => p.items)
    .find((t) => t.id === templateId);
  const inspect = useMutation({
    mutationFn: (generate: boolean) =>
      resourceApi.preview('templates', templateId, {
        recordId,
        connectionId,
        expectedRevision: selected!.revision,
        generate,
        published: true,
      }),
    onSuccess: setPreview,
  });
  const apply = useMutation({
    mutationFn: () =>
      resourceApi.apply(templateId, {
        previewId: preview!.id,
        idempotencyKey: crypto.randomUUID(),
        draftId: draft.id,
        expectedRevision: draft.revision,
        replaceEdited: replace,
      }),
    onSuccess: () => {
      setPreview(undefined);
      void client.invalidateQueries({ queryKey: emailKey() });
    },
  });
  if (draft.provenance?.origin === 'automation') return null;
  return (
    <details onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>Use template on this draft</summary>
      {open&&<p>A reply needs a template with a matching subject. A different subject can be prepared as a new draft from the record.</p>}
      {open && (
        <>
          <label>
            Template
            <select
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value);
                setPreview(undefined);
              }}
            >
              <option value="">Choose template</option>
              {templates.data?.pages
                .flatMap((p) => p.items)
                .filter((t) => t.published_version_id)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>
          </label>
          {templates.hasNextPage && (
            <button onClick={() => void templates.fetchNextPage()}>
              More templates
            </button>
          )}
          <label>
            Context record
            <select
              value={recordId}
              onChange={(e) => {
                setRecordId(e.target.value);
                setPreview(undefined);
              }}
            >
              <option value="">Choose associated record</option>
              {records.map((r) => (
                <option key={r.recordId} value={r.recordId}>
                  {r.displayName}
                </option>
              ))}
            </select>
          </label>
          <button
            disabled={!selected || !recordId || inspect.isPending}
            onClick={() =>
              inspect.mutate(
                (selected!.definition as TemplateDef).message.mode ===
                  'guidance',
              )
            }
          >
            Preview{' '}
            {selected &&
            (selected.definition as TemplateDef).message.mode === 'guidance'
              ? '· AI usage applies'
              : ''}
          </button>
          {preview && (
            <>
              <p>To: {preview.snapshot.resolved.address}</p>
              <p>{preview.result.subject}</p>
              <p style={{ whiteSpace: 'pre-wrap' }}>
                {preview.result.bodyText}
              </p>
              {preview.result.missing?.map((m, i) => (
                <p role="alert" key={i}>
                  {m.message}
                </p>
              ))}
              {draft.edited && (
                <label>
                  <input
                    type="checkbox"
                    checked={replace}
                    onChange={(e) => setReplace(e.target.checked)}
                  />
                  Replace edited draft and preserve prior revision
                </label>
              )}
              <button
                disabled={
                  apply.isPending ||
                  (draft.edited && !replace) ||
                  preview.result.outcome !== 'draft' ||
                  !!preview.result.missing?.length
                }
                onClick={() => apply.mutate()}
              >
                Apply to this draft
              </button>
            </>
          )}
          {[inspect.error, apply.error].filter(Boolean).map((e, i) => (
            <p role="alert" key={i}>
              {e!.message}
            </p>
          ))}
        </>
      )}
    </details>
  );
}
